import { useState, useEffect } from 'react'
import Modal from '../../../../../components/modal/Modal'
import { Icon } from '@iconify/react'
import { post, put } from '../../../../../utils/Api'
import { useGlobalContext } from '../../../../../utils/Context'
import FormLoader from '../../../../../components/loader/FormLoader'

interface RateModalProps {
  setShowModal: (v: boolean) => void
  fetchRates: () => void
  /** null = create mode, object = edit mode */
  selectedRate: any | null
  /** pre-fill zone when opening in create mode */
  prefilledZone?: string
}

const ZONE_OPTIONS = [
  'zone1',
  'zone2',
  'zone3',
  'zone4',
  'zone5',
  'zone6',
  'zone7',
  'zone8',
]

function RateModal({
  setShowModal,
  fetchRates,
  selectedRate,
  prefilledZone,
}: RateModalProps) {
  const { notify } = useGlobalContext()
  const [loading, setLoading] = useState(false)
  const isEdit = !!selectedRate

  const [form, setForm] = useState({
    zone: '',
    user: '',
    aggregator: '',
    superAggregator: '',
    special: '',
    trustedUser: '',
  })

  useEffect(() => {
    if (isEdit && selectedRate) {
      setForm({
        zone: selectedRate.zone ?? '',
        user: selectedRate.user ?? '',
        aggregator: selectedRate.aggregator ?? '',
        superAggregator: selectedRate.superAggregator ?? '',
        special: selectedRate.special ?? '',
        trustedUser: selectedRate.trustedUser ?? '',
      })
    } else if (prefilledZone) {
      setForm((f) => ({ ...f, zone: prefilledZone }))
    }
  }, [selectedRate, prefilledZone, isEdit])

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target
    setForm((f) => ({ ...f, [name]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const required: (keyof typeof form)[] = [
      'zone',
      'user',
      'aggregator',
      'superAggregator',
      'special',
    ]
    const missing = required.filter((k) => !String(form[k]).trim())
    if (missing.length) {
      notify('warn', `Required: ${missing.join(', ')}`)
      return
    }

    const payload: any = {
      user: Number(form.user),
      aggregator: Number(form.aggregator),
      superAggregator: Number(form.superAggregator),
      special: Number(form.special),
    }
    if (form.trustedUser) payload.trustedUser = Number(form.trustedUser)

    setLoading(true)
    try {
      let result: any
      if (isEdit) {
        result = await put(
          `heavy-weight-rates/update?id=${selectedRate._id}`,
          payload,
        )
      } else {
        result = await post(
          'heavy-weight-rates/create',
          { zone: form.zone, ...payload },
          null,
        )
      }

      if (result.status >= 200 && result.status <= 201) {
        notify('success', result.message ?? 'Rate saved')
        setShowModal(false)
        fetchRates()
      } else {
        notify('error', result.message ?? 'Something went wrong')
      }
    } catch {
      notify('error', 'Network error')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal>
      <div className='left-modal'>
        <div className='modal-top flex justify-between items-center'>
          <h2>
            {isEdit ? `Edit ${form.zone.toUpperCase()}` : 'Add Zone Rate'}
          </h2>
          <div className='close flex items-center justify-center'>
            <Icon icon='line-md:close' onClick={() => setShowModal(false)} />
          </div>
        </div>

        <form className='form' onSubmit={handleSubmit}>
          {/* Zone — read-only in edit, dropdown in create */}
          <div className='input-card'>
            <p>Zone</p>
            {isEdit ? (
              <input
                type='text'
                value={form.zone.toUpperCase()}
                readOnly
                style={{
                  background: 'var(--input-bg, #f5f5f5)',
                  cursor: 'not-allowed',
                }}
              />
            ) : (
              <select
                name='zone'
                value={form.zone}
                onChange={handleChange}
                required
              >
                <option value=''>— Select Zone —</option>
                {ZONE_OPTIONS.map((z) => (
                  <option key={z} value={z}>
                    {z.toUpperCase()}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className='input-card'>
            <p>User price per kg (₦)</p>
            <input
              type='number'
              name='user'
              min={0}
              placeholder='e.g. 6000'
              value={form.user}
              onChange={handleChange}
              required
            />
          </div>

          <div className='input-card'>
            <p>Aggregator price per kg (₦)</p>
            <input
              type='number'
              name='aggregator'
              min={0}
              placeholder='e.g. 5500'
              value={form.aggregator}
              onChange={handleChange}
              required
            />
          </div>

          <div className='input-card'>
            <p>Super-Aggregator price per kg (₦)</p>
            <input
              type='number'
              name='superAggregator'
              min={0}
              placeholder='e.g. 5000'
              value={form.superAggregator}
              onChange={handleChange}
              required
            />
          </div>

          <div className='input-card'>
            <p>Special price per kg (₦)</p>
            <input
              type='number'
              name='special'
              min={0}
              placeholder='e.g. 4500'
              value={form.special}
              onChange={handleChange}
              required
            />
          </div>

          <div className='input-card'>
            <p>
              Trusted User price per kg (₦){' '}
              <span
                style={{ fontWeight: 400, opacity: 0.6, fontSize: '0.8em' }}
              >
                — optional, defaults to User rate
              </span>
            </p>
            <input
              type='number'
              name='trustedUser'
              min={0}
              placeholder='Leave blank to use User rate'
              value={form.trustedUser}
              onChange={handleChange}
            />
          </div>

          <div className='input-card'>
            <br />
            {loading ? (
              <FormLoader position='center' />
            ) : (
              <button type='submit'>
                {isEdit ? 'Save Changes' : 'Create Rate'}
              </button>
            )}
          </div>
        </form>
      </div>
    </Modal>
  )
}

export default RateModal
